/**
 * Header row for a Studio tool panel: back arrow + title on the left, close on the
 * right. Verbatim from the prototype.
 */
import { ArrowLeft, X } from 'lucide-react';
import { c } from '../../styles/theme';

export interface DrawerHeaderProps {
  title: string;
  /** Returns to the tool list. */
  onBack: () => void;
  /** Closes the drawer entirely. */
  onClose: () => void;
  backLabel?: string;
}

export default function DrawerHeader({ title, onBack, onClose, backLabel }: DrawerHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        // A gap, so "Version History" and the Close cross cannot meet: with
        // space-between alone the two are only apart while there is slack, and at
        // 380px inside a panel there is none — the title ran into the cross.
        gap: 8,
        marginBottom: 6,
      }}
    >
      <button
        type="button"
        onClick={onBack}
        aria-label={`Back from ${title}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          // The half of the row that gives. `min-width: 0` releases the flex item's
          // automatic minimum size (its min-content width, which for "Tempo & Pitch" at
          // 15px is the whole phrase) so the title wraps inside the button instead of
          // pushing Close past the panel's padding.
          minWidth: 0,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <ArrowLeft size={14} color={c.text2} style={{ flexShrink: 0 }} />
        <span
          style={{
            fontFamily: 'Space Grotesk',
            fontWeight: 600,
            fontSize: 15,
            color: c.text1,
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
        >
          {backLabel ?? title}
        </span>
      </button>
      <button
        type="button"
        onClick={onClose}
        // Never shrinks: an 18px cross squeezed to 12px is the control a user needs
        // most on the layout where the panel fills the screen.
        style={{ background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}
        aria-label="Close"
      >
        <X size={18} color={c.text2} />
      </button>
    </div>
  );
}
